import type { VisitRecord } from "../../types/domain";
import { formatDate, formatTime } from "../../utils/dateTime";

type VisitListProps = {
  title: string;
  description: string;
  records: VisitRecord[];
  total: number | null;
  emptyMessage: string;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onEdit?: (record: VisitRecord) => void;
  onPreviewSignature: (path: string, label: string) => void;
};

function VisitStatus({ record }: { record: VisitRecord }) {
  if (record.status === "open") {
    return <span className="status-pill status-pill--open">In sede</span>;
  }

  if (!record.checkoutSignaturePath && record.checkoutClosedBy) {
    return (
      <span
        className="status-pill status-pill--admin"
        title="Uscita registrata da un amministratore senza firma del visitatore"
      >
        Chiusa da admin
      </span>
    );
  }

  return <span className="status-pill status-pill--closed">Chiusa</span>;
}

function formatCheckout(record: VisitRecord) {
  if (!record.checkoutAt) {
    return "—";
  }

  // Data esplicita solo se l'uscita non è nello stesso giorno dell'arrivo.
  return formatDate(record.checkoutAt) === formatDate(record.checkinAt)
    ? formatTime(record.checkoutAt)
    : `${formatDate(record.checkoutAt)} ${formatTime(record.checkoutAt)}`;
}

export function VisitList({
  title,
  description,
  records,
  total,
  emptyMessage,
  isRefreshing,
  isLoadingMore,
  onLoadMore,
  onEdit,
  onPreviewSignature,
}: VisitListProps) {
  const totalCount = total ?? records.length;
  const hasMore = records.length < totalCount;

  return (
    <section className="admin-panel">
      <div className="admin-panel__header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className="summary-chip">{totalCount} record</span>
      </div>
      {records.length === 0 ? (
        <div className="empty-state">{emptyMessage}</div>
      ) : (
        <div className={`data-table-wrap ${isRefreshing ? "is-refreshing" : ""}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Visitatore</th>
                <th scope="col">Data</th>
                <th scope="col">Arrivo</th>
                <th scope="col">Uscita</th>
                <th scope="col">Stato</th>
                <th scope="col">Note</th>
                <th scope="col" className="data-table__actions-head">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.visitId}>
                  <td className="data-table__primary">
                    <strong>
                      {record.firstName} {record.lastName}
                    </strong>
                    <span>{record.company}</span>
                  </td>
                  <td data-label="Data">{formatDate(record.checkinAt)}</td>
                  <td data-label="Arrivo">{formatTime(record.checkinAt)}</td>
                  <td data-label="Uscita">{formatCheckout(record)}</td>
                  <td data-label="Stato">
                    <VisitStatus record={record} />
                  </td>
                  <td data-label="Note" className="data-table__notes">
                    {record.notes?.trim() ? record.notes : <span className="muted">—</span>}
                  </td>
                  <td className="data-table__actions">
                    <button
                      className="ghost-button ghost-button--sm"
                      type="button"
                      onClick={() =>
                        onPreviewSignature(record.checkinSignaturePath, "Firma arrivo")
                      }
                    >
                      Firma arrivo
                    </button>
                    {record.checkoutSignaturePath ? (
                      <button
                        className="ghost-button ghost-button--sm"
                        type="button"
                        onClick={() => {
                          if (record.checkoutSignaturePath) {
                            onPreviewSignature(record.checkoutSignaturePath, "Firma uscita");
                          }
                        }}
                      >
                        Firma uscita
                      </button>
                    ) : null}
                    {onEdit ? (
                      <button
                        className="ghost-button ghost-button--sm ghost-button--brand"
                        type="button"
                        onClick={() => onEdit(record)}
                      >
                        Correggi
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {records.length > 0 ? (
        <div className="admin-panel__footer">
          <span>
            Mostrate {records.length} di {totalCount} visite
          </span>
          {hasMore ? (
            <button
              className="ghost-button ghost-button--sm"
              type="button"
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "Caricamento..." : "Carica altre"}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
